import { useEffect, useEffectEvent } from "react";

/**
 * Observes size changes of `target` and delivers them on the next animation
 * frame. Mutating layout synchronously inside a ResizeObserver callback
 * re-triggers the same delivery loop and fires "ResizeObserver loop completed
 * with undelivered notifications" — deferring to a frame avoids that.
 *
 * `target` can be an element, a ref, or `null` to disable observation
 * (e.g. `useResizeObserver(enabled ? ref : null, onResize)`). Deliveries are
 * coalesced: at most one `onResize` call per frame, with the latest entries.
 */
export const useResizeObserver = <T extends Element>(
  target: T | React.RefObject<T | null> | null,
  onResize: (entries: ResizeObserverEntry[]) => void,
): void => {
  const handleResize = useEffectEvent(onResize);

  useEffect(() => {
    const element: Element | null =
      target instanceof Element ? target : (target?.current ?? null);
    if (!element) return;

    let frame: number | undefined;
    const observer = new ResizeObserver((entries) => {
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
      }
      frame = requestAnimationFrame(() => {
        frame = undefined;
        handleResize(entries);
      });
    });
    observer.observe(element);

    return () => {
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
      }
      observer.disconnect();
    };
  }, [target]);
};
